import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';

import * as auth from 'ducks/auth';
import * as credentials from 'ducks/credentials';
import * as users from 'ducks/users';
import { FormTextField, Form } from 'new-design/common/ui/form-inputs';
import IntlMessages from 'util/IntlMessages';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import { ForgotPasswordSchema, SignInSchema } from './validationSchema';
import { CompanyLogo } from './components/CompanyLogo';
import { Submit } from './components/Submit';
import { useStyles } from './styles';
import { CompanyIcon, UserIcon, PasswordIcon, EyeShowIcon, EyeOffIcon, ArrowLeftIcon } from 'assets/icons';
import { Button, IconButton } from 'new-design/common/ui';
import { useQueryParams } from 'new-design/hooks/useQueryParams';
import { AnimatedCheckmark } from 'new-design/components/AnimatedCheckmark';

export const SignInForm = ({ setSubmit }) => {
  const classes = useStyles();
  const { accountName, forgotPassword: forgotPasswordParam } = useQueryParams();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(forgotPasswordParam || false);
  const [successMessage, setSuccessMessage] = useState();
  const [loading, setLoading] = useState(false);

  const credentialsState = useSelector(credentials.selectors);

  const { login } = bindActionCreators(auth.actions, useDispatch());
  const { requestPasswordReset } = bindActionCreators(users.actions, useDispatch());

  const onSubmit = useCallback(
    async ({ login: loginValues, forgotPassword: forgotPasswordValues }, { setSubmitting }) => {
      if (forgotPasswordMode) {
        setLoading(true);
        requestPasswordReset(forgotPasswordValues, {
          onSuccess: ({ message }) => setSuccessMessage(message),
          onFinally: () => setLoading(false),
        });
      } else {
        setSubmitting(true);
        try {
          login({
            ...loginValues,
            onFailure: () => setSubmitting(false),
          });
          // temp fix
          await setTimeout(() => {}, 2000);
        } catch {
          NotificationManager.error(<IntlMessages id="request_error" />);
          setSubmitting(false);
        }
      }
    },
    [forgotPasswordMode]
  );

  return (
    <Box className={classes.formContainer}>
      <Form
        initialValues={{
          login: {
            ...credentialsState,
            ...(accountName ? { company: accountName } : {}),
          },
          forgotPassword: {
            company: '',
            username: '',
          },
        }}
        validationSchema={forgotPasswordMode ? ForgotPasswordSchema : SignInSchema}
        onSubmit={onSubmit}
        outerSubmit={setSubmit}
      >
        <Box className={classes.formRoot}>
          <CompanyLogo />
          <Box className={classes.form}>
            {!forgotPasswordMode ? (
              <>
                <FormTextField
                  fullWidth
                  name="login.company"
                  autoFocus={!credentialsState.company}
                  topLabel={<IntlMessages id="login_company_hint" />}
                  className={classes.input}
                  icon={<CompanyIcon className={classes.greyIcon} />}
                />
                <FormTextField
                  fullWidth
                  name="login.user"
                  topLabel={<IntlMessages id="login_user_hint" />}
                  className={classes.input}
                  icon={<UserIcon className={classes.greyIcon} />}
                />
                <FormTextField
                  fullWidth
                  autoFocus={!!credentialsState.user}
                  name="login.password"
                  type={passwordVisible ? 'text' : 'password'}
                  topLabel={<IntlMessages id="login_password_hint" />}
                  className={classes.input}
                  icon={<PasswordIcon className={classes.greyIcon} />}
                  endIcon={
                    <IconButton onClick={() => setPasswordVisible((prev) => !prev)}>
                      {passwordVisible ? (
                        <EyeOffIcon className={classes.eyeIcon} />
                      ) : (
                        <EyeShowIcon className={classes.eyeIcon} />
                      )}
                    </IconButton>
                  }
                />
                <Box display="flex" width="100%" justifyContent="flex-end" mt={1}>
                  <Button
                    className={classes.forgotPasswordButton}
                    onClick={() => setForgotPasswordMode(true)}
                  >
                    <IntlMessages id="forgot_password" />
                  </Button>
                </Box>
                <Box className={classes.actions}>
                  <Submit
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.actionButton}
                  >
                    <IntlMessages id="login" />
                  </Submit>
                </Box>
              </>
            ) : (
              <>
                <>
                  {successMessage ? (
                    <Box className={classes.successContainer}>
                      <AnimatedCheckmark />
                      <Typography align="center">{successMessage}</Typography>
                    </Box>
                  ) : (
                    <>
                      <Box className={classes.forgotPasswordTitleContainer}>
                        <Typography variant="h5">
                          <IntlMessages id="forgot_password" />
                        </Typography>
                        <Typography>
                          <IntlMessages id="forgot_password_description" />
                        </Typography>
                      </Box>
                      <FormTextField
                        fullWidth
                        name="forgotPassword.company"
                        autoFocus={!credentialsState.company}
                        topLabel={<IntlMessages id="login_company_hint" />}
                        className={classes.input}
                        icon={<CompanyIcon className={classes.greyIcon} />}
                      />
                      <FormTextField
                        fullWidth
                        name="forgotPassword.username"
                        topLabel={<IntlMessages id="login_user_hint" />}
                        className={classes.input}
                        icon={<UserIcon className={classes.greyIcon} />}
                      />
                      <Box className={classes.actions}>
                        <Submit
                          fullWidth
                          type="submit"
                          variant="contained"
                          color="primary"
                          className={classes.actionButton}
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size="auto" className={classes.spinner} />
                          ) : (
                            <IntlMessages id="reset_password" />
                          )}
                        </Submit>
                      </Box>
                    </>
                  )}
                  <Box className={classes.actions} justifyContent="center">
                    <IconButton
                      className={classes.backToLoginButton}
                      onClick={() => {
                        setForgotPasswordMode(false);
                        setSuccessMessage(undefined);
                      }}
                    >
                      <ArrowLeftIcon />
                      <Typography>
                        <IntlMessages id="back_to_login" />
                      </Typography>
                    </IconButton>
                  </Box>
                </>
              </>
            )}
          </Box>
        </Box>
      </Form>
    </Box>
  );
};
